import { useEffect, useState } from "react";
import { api, formatIDR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Pencil, Trash2, FileDown, Share2, FileSignature, Copy } from "lucide-react";
import { generatePackagePDF, generateInternalPDF, buildWhatsAppText } from "../lib/pdf";
import { ProposalPdfDialog } from "../components/ProposalPdfDialog";

export default function Packages() {
    const [pkgs, setPkgs] = useState([]);
    const [settings, setSettings] = useState(null);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [pdfPkg, setPdfPkg] = useState(null); // paket yang dipilih untuk export proposal

    async function load() {
        setLoading(true);
        try {
            const [p, s] = await Promise.all([api.get("/packages"), api.get("/settings")]);
            setPkgs(p.data);
            setSettings(s.data);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    async function remove(pkg) {
        if (!window.confirm(`Hapus paket ${pkg.name}?`)) return;
        try {
            await api.delete(`/packages/${pkg.id}`);
            toast.success("Paket dihapus");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function duplicate(pkg) {
        try {
            const payload = {
                name: `${pkg.name} (Salinan)`,
                description: pkg.description || "",
                patient_target: pkg.patient_target || "",
                qty: pkg.qty || 1,
                items: pkg.items || [],
                discount_percent: pkg.discount_percent || 0,
                discount_type: pkg.discount_type || "percent",
                discount_amount: pkg.discount_amount || 0,
            };
            await api.post("/packages", payload);
            toast.success(`Paket "${pkg.name}" berhasil diduplikat`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const filtered = pkgs.filter((p) =>
        (p.name + " " + (p.description || "")).toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="packages-page">
            <div>
                <div className="label-uppercase mb-2">Paket Pemeriksaan</div>
                <h1 className="font-heading text-4xl tracking-tight font-semibold">
                    Paket Tersimpan
                </h1>
                <p className="text-[#4A5E57] mt-2">
                    Daftar paket pemeriksaan yang telah dibuat. Anda bisa mengunduh PDF atau membagikannya via WhatsApp kapan saja.
                </p>
            </div>

            <div className="flex items-center gap-3">
                <Input
                    placeholder="Cari paket..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="packages-search"
                    className="max-w-sm"
                />
                <Link to="/mcu" data-testid="packages-new-btn">
                    <Button className="bg-[#245B4B] hover:bg-[#1A4236] text-white">
                        + Paket Baru
                    </Button>
                </Link>
            </div>

            {loading ? (
                <div className="text-[#4A5E57]">Memuat...</div>
            ) : filtered.length === 0 ? (
                <div className="surface-card p-10 text-center text-[#6A9C89]">
                    Belum ada paket. Buat paket pertama Anda dari halaman Kalkulator Paket Pemeriksaan.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map((p) => (
                        <div
                            key={p.id}
                            className="surface-card p-6 flex flex-col"
                            data-testid={`pkg-card-${p.id}`}
                        >
                            <div className="flex-1">
                                <div className="label-uppercase mb-1">
                                    {p.items?.length || 0} pemeriksaan
                                </div>
                                <h3 className="font-heading text-xl font-medium mb-1">{p.name}</h3>
                                {p.patient_target && (
                                    <div className="text-xs text-[#6A9C89] mb-2">
                                        Target: {p.patient_target}
                                    </div>
                                )}
                                <p className="text-sm text-[#4A5E57] line-clamp-2">
                                    {p.description || "-"}
                                </p>
                                <div className="mt-4 pt-4 border-t border-[#D1DCD8] space-y-1">
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-xs text-[#6A9C89]">Harga Final / Pasien</span>
                                        <span className="font-heading text-2xl font-semibold text-[#1A362D]">
                                            {formatIDR(p.final_price)}
                                        </span>
                                    </div>
                                    {(p.qty || 1) > 1 && (
                                        <div className="flex items-baseline justify-between">
                                            <span className="text-xs text-[#6A9C89]">
                                                Grand Total ({p.qty} pasien)
                                            </span>
                                            <span className="font-heading text-lg font-semibold text-[#245B4B]">
                                                {formatIDR(p.grand_total || p.final_price * (p.qty || 1))}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-4">
                                <Link to={`/mcu/${p.id}`} data-testid={`pkg-edit-${p.id}`}>
                                    <Button
                                        variant="outline"
                                        className="w-full border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                    >
                                        <Pencil size={14} className="mr-1" /> Edit
                                    </Button>
                                </Link>
                                <Button
                                    variant="outline"
                                    onClick={() => setPdfPkg(p)}
                                    data-testid={`pkg-pdf-${p.id}`}
                                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    <FileDown size={14} className="mr-1" /> PDF
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => generateInternalPDF(p, settings)}
                                    data-testid={`pkg-internal-pdf-${p.id}`}
                                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    <FileSignature size={14} className="mr-1" /> Pengajuan
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        window.open(
                                            `https://wa.me/?text=${encodeURIComponent(
                                                buildWhatsAppText(p, settings)
                                            )}`,
                                            "_blank"
                                        )
                                    }
                                    data-testid={`pkg-wa-${p.id}`}
                                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    <Share2 size={14} className="mr-1" /> WA
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => duplicate(p)}
                                    data-testid={`pkg-duplicate-${p.id}`}
                                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    <Copy size={14} className="mr-1" /> Duplikat
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => remove(p)}
                                    data-testid={`pkg-delete-${p.id}`}
                                    className="border-[#D1DCD8] text-[#D9534F] hover:bg-red-50 hover:text-[#D9534F]"
                                >
                                    <Trash2 size={14} className="mr-1" /> Hapus
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <ProposalPdfDialog
                open={!!pdfPkg}
                onOpenChange={(v) => !v && setPdfPkg(null)}
                onExport={(opts) => generatePackagePDF(pdfPkg, settings, opts)}
            />
        </div>
    );
}
